import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Sparkles, Clock, ArrowRight, X, Mail, Check, Loader2, Lock } from "lucide-react";
import toast from "react-hot-toast";
import DemoConvertModal from "./DemoConvertModal";
import { authedFetch, API_BASE } from "../utils/authedFetch";
import { logoutUser } from "../store/slices/user.slice";
import { isDemoMembershipExpired, getDemoMembership } from "../utils/demoMembership";
import { track } from "../utils/analytics";

/**
 * DemoBanner — persistent strip at the top of the CRM for demo
 * accounts. Reminds the visitor that everything they're doing is
 * a temporary tour, surfaces an always-visible "Sign up" CTA, and
 * shows a live countdown until their demo expires.
 *
 * Mounted once at the App level so it sits above every CRM page.
 * Renders null for non-demo users so it has zero cost for the
 * 99% of sessions that aren't demos.
 *
 * Why a banner and not a modal: a modal blocks interaction and
 * would interrupt the very tour we want them to take. A persistent
 * banner is a constant gentle nudge without breaking flow.
 *
 * Dismissable for the current session only (sessionStorage, not
 * localStorage) so the next page-load re-asserts. We want the
 * conversion CTA in their face during the entire tour, not buried
 * behind a one-click hide-forever.
 */

const DISMISS_KEY = "__demo_banner_dismissed__";

function formatTimeLeft(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const mins = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (hours >= 1) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

export default function DemoBanner() {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.users?.user);
  const isDemo = Boolean(user?.isDemoAccount);
  // The visible countdown should reflect the ORIGINAL demo window, not
  // whatever fresh expiry POST /demo/start happens to return on a
  // re-login. Without this, logging out and logging back in re-seeded
  // `user.demoExpiresAt` to "24h from now" and the clock visibly
  // reset — exactly the loophole the membership record was supposed
  // to close (saveDemoMembership preserves the first-launch expiry
  // server-issuance isn't aware of). Pick whichever ends sooner so a
  // re-login can never extend the tour.
  const sessionExpiresAt = user?.demoExpiresAt;
  const membershipExpiresAt = getDemoMembership()?.demoExpiresAt;
  const expiresAt = [sessionExpiresAt, membershipExpiresAt]
    .filter(Boolean)
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0] || null;
  // DemoLauncher auto-registers the signup email with
  // /demo/capture-email when the tour starts, so most visitors
  // already have a reminder set. Hide the "Remind me" CTA in that
  // case — surfacing it would suggest they need to opt in when
  // they don't, and clicking it would just be a no-op overwrite.
  const reminderAlreadySet = Boolean(user?.demoNotificationEmail);

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );
  const [convertOpen, setConvertOpen] = useState(false);
  const [emailCaptureOpen, setEmailCaptureOpen] = useState(false);
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureSubmitting, setCaptureSubmitting] = useState(false);
  const [captureSaved, setCaptureSaved] = useState(false);
  const [, setTick] = useState(0);

  // Re-render once a minute so the countdown stays current — and so the
  // banner flips to the expired gate the moment the window closes. Runs
  // for every demo session (even a dismissed one), since the expiry
  // transition must be caught regardless of whether they hid the strip.
  useEffect(() => {
    if (!isDemo) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [isDemo]);

  if (!isDemo) return null;

  // ── Expired gate ──────────────────────────────────────────────────
  // A demo is "over" when either the live session's clock has run out
  // OR the persisted membership window (set at first launch, survives
  // logout, never extended) has elapsed. The membership check is what
  // makes the gate hold even if a re-login re-seeds a fresh session —
  // the account already spent its one window.
  const liveExpired = expiresAt && new Date(expiresAt).getTime() <= Date.now();
  const expired = liveExpired || isDemoMembershipExpired();

  if (expired) {
    return <DemoExpiredGate dispatch={dispatch} />;
  }

  if (dismissed) return null;

  const timeLeft = formatTimeLeft(expiresAt);
  const expiringSoon = timeLeft && /^(\d+)m left$/.test(timeLeft) &&
    parseInt(timeLeft.match(/^(\d+)m/)[1], 10) < 60;

  function handleDismiss() {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  async function handleSubmitEmail(e) {
    e.preventDefault();
    if (!captureEmail || captureSubmitting) return;
    setCaptureSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/demo/capture-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: captureEmail.trim().toLowerCase() }),
      });
      const body = await res.json();
      if (!res.ok || !body?.success) throw new Error(body?.message || "Couldn't save");
      track("demo_email_captured");
      setCaptureSaved(true);
      toast.success("We'll email you before your tour expires.");
      setTimeout(() => setEmailCaptureOpen(false), 1200);
    } catch (err) {
      toast.error(err.message || "Couldn't save");
    } finally {
      setCaptureSubmitting(false);
    }
  }

  return (
    <>
      <div
        role="status"
        className={
          "relative w-full text-white text-sm border-b " +
          (expiringSoon
            ? "bg-gradient-to-r from-amber-500/95 to-amber-600/95 border-amber-700/40"
            : "bg-gradient-to-r from-[#1ec8c8] to-emerald-400 border-[#1ec8c8]/30")
        }
      >
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-black flex-shrink-0" />
            <p className="text-black font-semibold text-xs sm:text-sm min-w-0">
              {/* The old copy ("click around freely, nothing here is permanent")
                  read as an invitation to add things, so visitors filled in a
                  whole form before the guard told them saving needs a real CRM.
                  The demo deliberately does not accept your own records — it is
                  a tour of sample data — so say that here, once, before anyone
                  types, rather than after. */}
              <span className="hidden sm:inline">You're touring Stacked in demo mode — </span>
              <span className="sm:hidden">Demo mode — </span>
              explore anything you like. Adding your own records comes with your own CRM.
            </p>
            {timeLeft && (
              <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-bold text-black bg-black/15 px-2 py-0.5 rounded-full ml-1">
                <Clock className="w-3 h-3" />
                {timeLeft}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Soft email capture — visible only when the reminder
                hasn't been auto-registered yet. With the gated demo
                flow, DemoLauncher posts /demo/capture-email with the
                signup email immediately, so this is rarely shown.
                Kept as a manual fallback for the case where that
                auto-post fails (network, server) or where a future
                code path lets demos start without a signup email
                handy. */}
            {!reminderAlreadySet && (
              <button
                type="button"
                onClick={() => setEmailCaptureOpen(true)}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/10 hover:bg-black/20 text-black text-xs font-semibold transition-colors"
              >
                <Mail className="w-3 h-3" />
                Remind me
              </button>
            )}
            {/* Opens the conversion modal (POST /demo/convert).
                Visitor is already signed up (gated demo flow), so
                this is the plan-pick + payment step that turns the
                ephemeral demo into a real subscribed account.
                Preserves their tour data through conversion. */}
            <button
              type="button"
              onClick={() => setConvertOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black text-white hover:bg-gray-900 text-xs font-bold transition-colors"
            >
              Pick a plan to keep going
              <ArrowRight className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Hide banner for this page"
              className="p-1 text-black/60 hover:text-black"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <DemoConvertModal open={convertOpen} onClose={() => setConvertOpen(false)} />

      {/* Email-capture mini-modal. Lightweight: one field, one button.
          Stores the email on user.demoNotificationEmail so the
          expiration scheduler can deliver a real reminder. */}
      {emailCaptureOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setEmailCaptureOpen(false)}
        >
          <div
            className="bg-[#141b2b] border border-white/10 rounded-2xl max-w-sm w-full shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#1ec8c8]/15 border border-[#1ec8c8]/30 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#1ec8c8]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm">Remind me before this expires</h3>
                <p className="text-gray-400 text-xs mt-0.5">One reminder, then we're done.</p>
              </div>
            </div>

            {captureSaved ? (
              <div className="flex items-center gap-2 text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-sm">
                <Check className="w-4 h-4" />
                Saved — we'll email you about an hour before expiration.
              </div>
            ) : (
              <form onSubmit={handleSubmitEmail} className="space-y-3">
                <input
                  type="email"
                  required
                  autoFocus
                  value={captureEmail}
                  onChange={(e) => setCaptureEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="w-full bg-[var(--lighter-dark)] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-500 focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 focus:outline-none"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={captureSubmitting}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#1ec8c8] hover:bg-[#34b8b3] text-black text-sm font-semibold disabled:opacity-50"
                  >
                    {captureSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    {captureSubmitting ? "Saving…" : "Save reminder"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEmailCaptureOpen(false)}
                    className="px-3 py-2.5 text-gray-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-[11px] text-gray-500">
                  Used once — to send you a reminder email. We won't add you to a list.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * DemoExpiredGate — the blocking "your tour is over" screen.
 *
 * Rendered by DemoBanner over the whole app once a demo's window has
 * elapsed. The demo dashboard stays visible behind the scrim, so the
 * product is still the backdrop, but every interaction is blocked until
 * the visitor picks a plan (which converts the tour into their real
 * account) or logs out. This is the behavior the demo flow promises on
 * re-login: the tour "pops up," then a pop-up explains it expired and
 * that continuing means choosing a plan.
 *
 * A converted/subscribed user never reaches this gate — getNavigation
 * routes anyone holding a real plan to their CRM instead of the demo.
 */
function DemoExpiredGate({ dispatch }) {
  const [convertOpen, setConvertOpen] = useState(false);

  useEffect(() => {
    track("demo_expired_gate_shown");
  }, []);

  function handleLogout() {
    dispatch(logoutUser());
    // Full reload to drop every RTK Query cache (matches the sidebar's
    // logout). Land on /login so they can sign back in once subscribed.
    window.location.href = "/login";
  }

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-expired-title"
        className="fixed inset-0 z-[10002] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      >
        <div className="bg-[#141b2b] border border-[#1ec8c8]/30 rounded-2xl max-w-md w-full shadow-2xl p-7 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#1ec8c8]/15 border border-[#1ec8c8]/30 flex items-center justify-center mb-4">
            <Lock className="w-7 h-7 text-[#1ec8c8]" />
          </div>
          <h2 id="demo-expired-title" className="text-white font-bold text-xl mb-2">
            Your demo has ended
          </h2>
          <p className="text-gray-300 text-sm mb-6 leading-relaxed">
            Hope you liked the tour. The demo was a sandbox to look around —
            pick a plan and we'll set you up with your own CRM, ready for
            your real contacts and deals.
          </p>
          <button
            type="button"
            onClick={() => { track("demo_expired_convert_click"); setConvertOpen(true); }}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1ec8c8] hover:bg-[#34b8b3] text-black font-bold transition-colors"
          >
            Pick a plan to keep going
            <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href="/pricing"
            className="block mt-3 text-sm text-gray-300 hover:text-white underline"
          >
            View all plans
          </a>
          <button
            type="button"
            onClick={handleLogout}
            className="block w-full mt-4 text-xs text-gray-500 hover:text-gray-300"
          >
            Log out
          </button>
        </div>
      </div>

      <DemoConvertModal open={convertOpen} onClose={() => setConvertOpen(false)} />
    </>
  );
}
